import { motion } from "framer-motion";
import { ArrowRight, Droplet, GlassWater, Mountain, Sparkles } from "lucide-react";
import { useLang } from "../i18n";
import { scrollToId } from "../smooth";
import { Divider, EASE, Reveal } from "./Reveal";

const Feature = ({ icon: Icon, lines, dark }) => (
    <div className="flex items-center gap-3.5">
        <Icon size={26} strokeWidth={1.1} className="shrink-0 text-champagne" />
        <span
            className={`text-[10px] leading-relaxed tracking-[0.22em] uppercase ${
                dark ? "text-ivory/75" : "text-ink/70"
            }`}
        >
            {lines[0]}
            <br />
            {lines[1]}
        </span>
    </div>
);

const Panel = ({ dark, image, name, text, f1, f2, cta, testid, bg }) => (
    <motion.article
        data-testid={testid}
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 1.15, ease: EASE }}
        style={{ backgroundColor: bg }}
        className={`flex flex-col items-center gap-8 border p-8 sm:flex-row sm:p-10 md:p-12 ${
            dark ? "border-champagne/20" : "border-ink/10"
        }`}
    >
        <motion.img
            src={image}
            alt={`LARIVA ${name}`}
            loading="lazy"
            initial={{ opacity: 0, y: 26 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, delay: 0.15, ease: EASE }}
            className="h-64 w-auto object-contain md:h-80"
        />
        <div className={dark ? "text-ivory" : "text-ink"}>
            <h3 className="font-serif text-2xl tracking-[0.22em] uppercase md:text-3xl">{name}</h3>
            <div className="mt-3 flex items-center gap-2.5">
                <span className="h-px w-8 bg-champagne" />
                <span className="h-1 w-1 rotate-45 bg-champagne" />
            </div>
            <p className={`mt-5 max-w-xs text-sm leading-relaxed ${dark ? "text-ivory/75" : "text-ink/75"}`}>
                {text}
            </p>
            <div className="mt-7 flex flex-wrap gap-x-8 gap-y-4">
                <Feature icon={dark ? Sparkles : Droplet} lines={f1} dark={dark} />
                <Feature icon={dark ? GlassWater : Mountain} lines={f2} dark={dark} />
            </div>
            <a
                href="#request"
                onClick={(e) => {
                    e.preventDefault();
                    scrollToId("#request");
                }}
                data-testid={dark ? "discover-sparkling-cta" : "discover-still-cta"}
                className={`group mt-8 inline-flex items-center gap-3 text-[11px] tracking-[0.28em] uppercase ${
                    dark ? "text-champagne" : "text-ink"
                }`}
            >
                <span className="border-b border-transparent pb-1 transition-colors duration-500 group-hover:border-champagne">
                    {cta}
                </span>
                <ArrowRight
                    size={14}
                    strokeWidth={1.6}
                    className="text-champagne transition-transform duration-500 group-hover:translate-x-1.5"
                />
            </a>
        </div>
    </motion.article>
);

const Collection = () => {
    const { t } = useLang();
    return (
        <section id="collection" data-testid="collection-section" className="bg-ivory px-5 py-20 md:px-10 md:py-28">
            <Reveal className="text-center">
                <h2 className="font-serif text-3xl tracking-[0.24em] text-ink uppercase md:text-4xl">
                    {t.collection.title}
                </h2>
                <div className="mt-5">
                    <Divider />
                </div>
            </Reveal>
            <div className="mx-auto mt-14 grid max-w-6xl gap-6 lg:grid-cols-2">
                <Panel
                    testid="panel-still"
                    image="/assets/bottle-still.jpg"
                    bg="#EBE0DA"
                    name={t.collection.still.name}
                    text={t.collection.still.text}
                    f1={t.collection.still.f1}
                    f2={t.collection.still.f2}
                    cta={t.collection.still.cta}
                />
                <Panel
                    testid="panel-sparkling"
                    dark
                    image="/assets/bottle-sparkling.jpg"
                    bg="#10263B"
                    name={t.collection.sparkling.name}
                    text={t.collection.sparkling.text}
                    f1={t.collection.sparkling.f1}
                    f2={t.collection.sparkling.f2}
                    cta={t.collection.sparkling.cta}
                />
            </div>
        </section>
    );
};

export default Collection;
